import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const pageHeaderSchema = z.object({
  variant: z.enum(['simple', 'split']).default('simple'),
  theme: z.enum(['dark', 'light']).default('dark'),
  eyebrow: z.string().max(120).optional(),
  title: z.string().min(1).max(300),
  highlight: z.string().max(160).optional(),
  subtitle: z.string().max(800).optional(),
  ctas: z
    .array(
      z.object({
        label: z.string().min(1).max(80),
        href: z.string().min(1).max(500),
        style: z.enum(['primary', 'ghost']).default('primary'),
      }),
    )
    .max(3)
    .optional(),
  stats: z
    .array(
      z.object({
        value: z.string().min(1).max(40),
        label: z.string().min(1).max(120),
      }),
    )
    .max(6)
    .optional(),
  image: z
    .object({
      src: z.string().url().or(z.string().startsWith('/')),
      alt: z.string().max(200).optional(),
      badge: z.string().max(80).optional(),
    })
    .optional(),
});

export type PageHeaderData = z.infer<typeof pageHeaderSchema>;

export function PageHeaderRenderer({
  variant = 'simple',
  theme = 'dark',
  eyebrow,
  title,
  highlight,
  subtitle,
  ctas,
  stats,
  image,
}: PageHeaderData) {
  const wrapperCls = [
    'v5-ph',
    theme === 'dark' ? 'v5-ph-dark' : 'v5-ph-light',
    variant === 'split' ? 'v5-ph-split' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const Content = (
    <>
      {eyebrow ? <div className="v5-ph-eyebrow">{eyebrow}</div> : null}
      <h1 className="v5-ph-h1">
        {title}
        {highlight ? <> <span className="hl-lime">{highlight}</span></> : null}
      </h1>
      {subtitle ? <p className="v5-ph-sub">{subtitle}</p> : null}
      {ctas && ctas.length > 0 ? (
        <div className="v5-ph-ctas">
          {ctas.map((cta, i) => (
            <a
              key={i}
              href={cta.href}
              className={`v5-ph-cta v5-ph-cta-${cta.style}`}
            >
              {cta.label}
              {cta.style === 'primary' ? (
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M3 8h10M9 4l4 4-4 4" />
                </svg>
              ) : null}
            </a>
          ))}
        </div>
      ) : null}
      {stats && stats.length > 0 ? (
        <div className={`v5-ph-stats${variant === 'split' ? ' v5-ph-stats-split' : ''}`}>
          {stats.map((s, i) => (
            <div key={i} className="v5-ph-stat">
              <div className="v5-ph-stat-num">{s.value}</div>
              <div className="v5-ph-stat-lbl">{s.label}</div>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );

  if (variant === 'split') {
    return (
      <section className={wrapperCls}>
        <div className="container">
          <div className="v5-ph-split-grid">
            <div className="v5-ph-split-left">{Content}</div>
            <div className="v5-ph-split-right">
              {image ? (
                <div className="v5-ph-img-frame">
                  <div className="v5-ph-img-glow"></div>
                  <img src={image.src} alt={image.alt ?? ''} loading="eager" />
                  {image.badge ? (
                    <div className="v5-ph-img-badge">
                      <span className="dot"></span>
                      {image.badge}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={wrapperCls}>
      <div className="container">{Content}</div>
    </section>
  );
}

export const pageHeaderBlock: BlockDefinition<PageHeaderData> = {
  meta: {
    type: 'page-header',
    label: 'Cabecera de página (v5)',
    description: 'Hero/cabecera con eyebrow, h1, subtítulo, CTAs y stats. Variantes simple o split (con imagen).',
    category: 'hero',
    icon: 'layout',
  },
  schema: pageHeaderSchema,
  defaults: {
    variant: 'split',
    theme: 'dark',
    eyebrow: '● Línea 02 · Compraventa · Vender',
    title: 'Vender tu empresa es importante.',
    highlight: 'Que esté bien acompañada, también.',
    subtitle:
      '17 años ayudando a empresarios a vender con la valoración correcta, el comprador correcto y las condiciones correctas. Confidencial desde el primer correo.',
    ctas: [
      { label: 'Solicita valoración gratis', href: '/valorar-empresa#formulario', style: 'primary' },
      { label: 'Llamada confidencial', href: '/contacto#agendar-ma', style: 'ghost' },
    ],
    stats: [
      { value: '+80', label: 'empresas vendidas' },
      { value: '92%', label: 'tasa de cierre' },
      { value: '6,4m', label: 'plazo medio' },
      { value: 'NDA', label: 'primer contacto' },
    ],
    image: {
      src: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=900&h=900&fit=crop&q=85&auto=format',
      alt: 'Empresario sénior en oficina',
      badge: '17 años · NDA siempre',
    },
  },
  fields: [
    {
      name: 'variant',
      label: 'Variante',
      type: 'select',
      options: [
        { label: 'Simple (sin imagen)', value: 'simple' },
        { label: 'Split (con imagen a la derecha)', value: 'split' },
      ],
    },
    {
      name: 'theme',
      label: 'Tema',
      type: 'select',
      options: [
        { label: 'Oscuro (negro)', value: 'dark' },
        { label: 'Claro (blanco)', value: 'light' },
      ],
    },
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'title', label: 'Título H1', type: 'text', required: true, max: 300 },
    { name: 'highlight', label: 'Texto destacado (lima)', type: 'text', max: 160 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 800 },
    {
      name: 'ctas',
      label: 'CTAs',
      type: 'array',
      max: 3,
      itemFields: [
        { name: 'label', label: 'Texto', type: 'text', required: true, max: 80 },
        { name: 'href', label: 'URL', type: 'url', required: true },
        {
          name: 'style',
          label: 'Estilo',
          type: 'select',
          options: [
            { label: 'Primario (lima)', value: 'primary' },
            { label: 'Ghost (transparente)', value: 'ghost' },
          ],
        },
      ],
    },
    {
      name: 'stats',
      label: 'Stats (cifras pequeñas)',
      type: 'array',
      max: 6,
      itemFields: [
        { name: 'value', label: 'Valor', type: 'text', required: true, max: 40 },
        { name: 'label', label: 'Etiqueta', type: 'text', required: true, max: 120 },
      ],
    },
    {
      name: 'image',
      label: 'Imagen (solo variante split)',
      type: 'object',
      fields: [
        { name: 'src', label: 'URL imagen', type: 'url', required: true },
        { name: 'alt', label: 'Alt text', type: 'text', max: 200 },
        { name: 'badge', label: 'Badge sobre imagen', type: 'text', max: 80 },
      ],
    },
  ],
  Renderer: PageHeaderRenderer,
};
